import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Mail, MapPin, Cake, Briefcase, Ticket as TicketIcon, ShieldCheck, ShieldOff, Crown, KeyRound, Gift, Send, FileText } from "lucide-react";
import { formatEventDate } from "@/lib/utils";
import ComposeEmailDialog from "@/components/ComposeEmailDialog";
import ConfirmationViewerDialog from "@/components/ConfirmationViewerDialog";

export default function AdminUserDetail() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/detail`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load user");
      nav("/admin");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [userId]);

  const grantVip = async () => {
    const isVip = !!data.user.is_vip;
    if (!window.confirm(isVip ? "Revoke VIP status?" : "Grant complimentary VIP status?")) return;
    try { await api.patch(`/admin/users/${userId}`, { is_vip: !isVip }); toast.success(isVip ? "VIP revoked" : "VIP granted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const sendReset = async () => {
    if (!window.confirm(`Send a password reset email to ${data.user.email}?`)) return;
    try { await api.post(`/admin/users/${userId}/send-reset`); toast.success(`Reset link emailed to ${data.user.email}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const suspend = async () => {
    const isSuspended = !!data.user.suspended;
    if (isSuspended) {
      if (!window.confirm("Reactivate this account?")) return;
      try { await api.post(`/admin/users/${userId}/unsuspend`); toast.success("Reactivated"); load(); }
      catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
      return;
    }
    const reason = window.prompt("Suspension reason (shown to the user on login):", "");
    if (reason === null) return;
    try { await api.post(`/admin/users/${userId}/suspend`, { reason }); toast.success("Suspended"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const [giftOpen, setGiftOpen] = useState(false);
  const [giftForm, setGiftForm] = useState({ type: "percent", percent: 15, amount: 1000, expires_in_days: 30, note: "" });
  const [giftBusy, setGiftBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [confViewerTicketId, setConfViewerTicketId] = useState(null);
  const sendPromoGift = async () => {
    setGiftBusy(true);
    try {
      const body = {
        discount_type: giftForm.type,
        amount: giftForm.type === "percent" ? Number(giftForm.percent) : Number(giftForm.amount),
        expires_in_days: Number(giftForm.expires_in_days) || null,
        personal_note: giftForm.note || null,
      };
      const { data } = await api.post(`/admin/users/${userId}/assign-promo`, body);
      toast.success(`Sent ${data.code} to ${data.email}`);
      setGiftOpen(false);
      setGiftForm({ type: "percent", percent: 15, amount: 1000, expires_in_days: 30, note: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    setGiftBusy(false);
  };

  if (loading || !data) return <div className="max-w-5xl mx-auto px-6 py-20 text-neutral-500" data-testid="user-detail-loading">Loading…</div>;

  const u = data.user;
  const groupedQ = data.questionnaire.reduce((acc, q) => {
    const cat = q.category || "General";
    (acc[cat] = acc[cat] || []).push(q);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="admin-user-detail-page">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-black mb-6"><ArrowLeft size={14} /> Back to admin</Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-6">
        <div className="flex gap-5 items-start">
          <div className="w-20 h-20 rounded-full bg-neutral-200 overflow-hidden shrink-0">
            {u.profile_photo_url ? <img src={u.profile_photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u.profile_photo_url}` : u.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-2xl font-heading">{(u.name || "?").charAt(0)}</div>}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-4xl" data-testid="user-detail-name">{u.name || "—"}</h1>
              {u.is_vip && <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full"><Crown size={12} /> VIP</span>}
              {u.suspended && <span className="text-xs font-medium bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full" data-testid="user-detail-suspended-badge">Suspended</span>}
              {u.role === "admin" && <span className="text-xs font-medium bg-black text-white px-2 py-0.5 rounded-full">Admin</span>}
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8 text-sm text-neutral-700" data-testid="user-detail-demographics">
              <div className="flex items-center gap-2"><Mail size={14} className="text-neutral-400" /> <span className="font-mono">{u.email}</span></div>
              {u.city && <div className="flex items-center gap-2"><MapPin size={14} className="text-neutral-400" /> {u.city}</div>}
              {u.age && <div className="flex items-center gap-2"><Cake size={14} className="text-neutral-400" /> {u.age} yrs · DOB {u.date_of_birth || "—"}</div>}
              {u.occupation && <div className="flex items-center gap-2"><Briefcase size={14} className="text-neutral-400" /> {u.occupation}</div>}
              <div className="flex items-center gap-2 text-neutral-500 text-xs">Joined {u.created_at ? formatEventDate(u.created_at.slice(0, 10), { month: "long", day: "numeric", year: "numeric" }) : "—"} via {u.auth_provider || "email"}</div>
              {u.suspended_reason && <div className="col-span-2 mt-1 text-xs text-rose-700 bg-rose-50 rounded-lg px-3 py-2">Suspension reason: {u.suspended_reason}</div>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0 md:max-w-md md:justify-end">
          <Dialog open={giftOpen} onOpenChange={setGiftOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100" data-testid="detail-gift-btn"><Gift size={14} className="mr-2" /> Send promo code</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Gift a promo code</DialogTitle>
                <DialogDescription className="text-sm text-neutral-500">Creates a single-use Stripe code and emails it to {u.name} at {u.email}.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <div className="text-eyebrow mb-1">Discount type</div>
                  <div className="flex gap-2">
                    <label className={`flex-1 text-sm rounded-xl border px-3 py-2 cursor-pointer text-center ${giftForm.type==="percent" ? "border-black bg-black text-white" : "border-neutral-200"}`}>
                      <input type="radio" name="gifttype" checked={giftForm.type==="percent"} onChange={()=>setGiftForm({...giftForm, type:"percent"})} className="hidden" data-testid="gift-type-percent" />
                      % off
                    </label>
                    <label className={`flex-1 text-sm rounded-xl border px-3 py-2 cursor-pointer text-center ${giftForm.type==="fixed" ? "border-black bg-black text-white" : "border-neutral-200"}`}>
                      <input type="radio" name="gifttype" checked={giftForm.type==="fixed"} onChange={()=>setGiftForm({...giftForm, type:"fixed"})} className="hidden" data-testid="gift-type-fixed" />
                      $ off
                    </label>
                  </div>
                </div>
                {giftForm.type === "percent" ? (
                  <div>
                    <div className="text-eyebrow mb-1">Percent off</div>
                    <Input type="number" min="1" max="100" value={giftForm.percent} onChange={e=>setGiftForm({...giftForm, percent: e.target.value})} className="rounded-xl" data-testid="gift-percent" />
                  </div>
                ) : (
                  <div>
                    <div className="text-eyebrow mb-1">Amount off (cents)</div>
                    <Input type="number" min="1" value={giftForm.amount} onChange={e=>setGiftForm({...giftForm, amount: e.target.value})} className="rounded-xl" data-testid="gift-amount" />
                    <div className="text-[11px] text-neutral-500 mt-1">1000 = $10.00</div>
                  </div>
                )}
                <div>
                  <div className="text-eyebrow mb-1">Expires in <span className="text-neutral-400 text-xs">days</span></div>
                  <Input type="number" min="1" max="365" value={giftForm.expires_in_days} onChange={e=>setGiftForm({...giftForm, expires_in_days: e.target.value})} className="rounded-xl" data-testid="gift-expiry" />
                </div>
                <div>
                  <div className="text-eyebrow mb-1">Personal note <span className="text-neutral-400 text-xs">(optional)</span></div>
                  <Textarea rows={3} placeholder="Sorry we had to reschedule last time — hope to see you soon." value={giftForm.note} onChange={e=>setGiftForm({...giftForm, note: e.target.value})} className="rounded-xl" data-testid="gift-note" />
                </div>
                <Button onClick={sendPromoGift} disabled={giftBusy} className="w-full rounded-full bg-black text-white btn-pill" data-testid="gift-send-btn">{giftBusy ? "Sending…" : "Create & email code"}</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={()=>setComposeOpen(true)} className="rounded-full" data-testid="detail-compose-btn"><Send size={14} className="mr-2" /> Compose email</Button>
          <Button variant="outline" onClick={grantVip} className="rounded-full" data-testid="detail-vip-btn"><Crown size={14} className="mr-2" /> {u.is_vip ? "Revoke VIP" : "Grant VIP"}</Button>
          <Button variant="outline" onClick={sendReset} className="rounded-full" data-testid="detail-reset-btn"><KeyRound size={14} className="mr-2" /> Send reset</Button>
          <Button variant="outline" onClick={suspend} className={`rounded-full ${u.suspended ? "" : "text-rose-600 border-rose-200 hover:bg-rose-50"}`} data-testid="detail-suspend-btn">
            {u.suspended ? <><ShieldCheck size={14} className="mr-2" /> Reactivate</> : <><ShieldOff size={14} className="mr-2" /> Suspend</>}
          </Button>
        </div>
      </div>

      {/* Tickets */}
      <section className="mb-12" data-testid="user-detail-tickets">
        <div className="flex items-center gap-2 mb-4">
          <TicketIcon size={16} />
          <h2 className="font-heading text-2xl">Ticket history</h2>
          <span className="text-sm text-neutral-500">({data.tickets.length})</span>
        </div>
        {data.tickets.length === 0 ? (
          <div className="card-elegant p-6 text-sm text-neutral-500">No tickets yet.</div>
        ) : (
          <div className="grid gap-3">
            {data.tickets.map(t => (
              <div key={t.ticket_id} className="card-elegant p-4 flex items-center justify-between gap-4">
                <div>
                  <div className="font-medium">{t.event?.title || "(event deleted)"}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {t.event?.date && <>{formatEventDate(t.event.date, { month: "short", day: "numeric", year: "numeric" })} · {t.event.venue}</>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-mono text-xs">{t.ticket_code}</div>
                    <div className={`text-[11px] uppercase tracking-widest mt-1 ${t.status === "paid" ? "text-emerald-700" : t.status === "refunded" ? "text-neutral-500" : "text-amber-700"}`}>{t.status}</div>
                  </div>
                  {t.status === "paid" && (
                    <Button size="sm" variant="outline" onClick={()=>setConfViewerTicketId(t.ticket_id)} data-testid={`detail-view-conf-${t.ticket_id}`} title="View / copy / resend confirmation"><FileText size={12} /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Questionnaire answers */}
      <section data-testid="user-detail-questionnaire">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="font-heading text-2xl">Compatibility answers</h2>
          <span className="text-sm text-neutral-500">({data.questionnaire.filter(q => q.answer != null).length}/{data.questionnaire.length} answered)</span>
        </div>
        {Object.entries(groupedQ).map(([cat, questions]) => (
          <div key={cat} className="mb-8">
            <div className="text-eyebrow mb-3">{cat}</div>
            <div className="grid gap-3">
              {questions.map(q => {
                const chosen = q.options?.[q.answer];
                return (
                  <div key={q.question_id} className="card-elegant p-4">
                    <div className="text-sm text-neutral-800">{q.question}</div>
                    {q.answer != null ? (
                      <div className="mt-2 text-sm font-medium text-black bg-cream/60 rounded-lg px-3 py-2" data-testid={`q-answer-${q.question_id}`}>
                        {chosen ?? String(q.answer)}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-neutral-400 italic">Not answered</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <ComposeEmailDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        mode="user"
        targetId={userId}
        recipientLabel={`${u.name || "user"} (${u.email})`}
        recipientCount={1}
        testId="compose-user-email"
      />
      <ConfirmationViewerDialog
        open={!!confViewerTicketId}
        onOpenChange={(v)=>{ if (!v) setConfViewerTicketId(null); }}
        ticketId={confViewerTicketId}
      />
    </div>
  );
}
